import { Checkbox, Space, Switch, Tooltip, message } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import { DownloadOutlined } from "@ant-design/icons";
import ModalItem from "./ModalItem";
import ButtonItem from "./ButtonItem";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import polizasDocumentoPdf from "../Pdf/polizasDocumentoPdf";
import polizasTrazabilidadPdf from "../Pdf/polizasTrazabilidadPdf";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/*
  Todas las pólizas del ciclo de vida de un documento en una sola hoja, de la más reciente a la
  más antigua. Dos modos:

  - Documento (props `tipo` + `documentoId`): resuelve la trazabilidad completa con
    polizas/polizasDeDocumento y luego arma el PDF. Lo usan los botones de Venta, Orden de
    Compra, Factura de Caja Chica y Factura sin Orden de Compra.
  - Movimiento (prop `polizasId`): se salta la trazabilidad porque el controlador que acaba de
    correr ya devolvió los ids. Lo usa la apertura automática al terminar un movimiento.

  En modo movimiento hay además un check "Ver pólizas de la trazabilidad" que sí resuelve la
  cadena completa (polizas/polizasDeTrazabilidadMovimiento deduce el documento a partir de esas
  mismas pólizas) y la imprime en el formato compacto de Reposición de Caja Chica. En modo
  documento no se dibuja: ahí la trazabilidad ya se muestra siempre.

  El PDF se arma en el navegador, igual que en ModalPdfPoliza y VerPolizasFactura.
*/
let contadorInstancias = 0;

class ModalPolizasDocumento extends Component {
  iframeRef = React.createRef();
  // Hay dos instancias vivas a la vez: la de la pantalla del documento y la global del Panel
  // (la que abre sola al terminar un movimiento). Sin esto compartirían el id del iframe.
  iframeName = "PolizasDocumentoIframe" + ++contadorInstancias;
  urlPdf = null;
  state = {
    loading: false,
    pdf: null,
    polizasId: null,
    detallada: false,
    trazabilidad: false,
  };

  componentDidUpdate(prevProps) {
    const cambioDocumento =
      this.props.documentoId !== prevProps.documentoId ||
      this.props.tipo !== prevProps.tipo ||
      this.props.polizasId !== prevProps.polizasId;

    if (this.props.visible && (!prevProps.visible || cambioDocumento)) {
      this.consultar();
    } else if (!this.props.visible && prevProps.visible) {
      // Limpiar para que al reabrir con otro documento siempre se re-consulte
      this.setState({ pdf: null, polizasId: null, detallada: false, trazabilidad: false });
    }
  }

  consultar() {
    // En modo movimiento los ids ya vienen dados: se muestran tal cual, y la trazabilidad sólo
    // se resuelve si el usuario prende el check.
    if (this.props.polizasId?.length) {
      this.setState({ polizasId: this.props.polizasId, trazabilidad: false }, () =>
        this.cargarPolizas(false)
      );
      return;
    }
    if (!this.props.tipo || !this.props.documentoId) return;

    this.setState({ loading: true });
    axios
      .post(
        "polizas/polizasDeDocumento",
        {
          tipo: this.props.tipo,
          documentoId: this.props.documentoId,
          empresaId: this.props.empresaId,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (!res.data?.polizasId || res.data.polizasId.length === 0) {
          message.warning("Este documento no tiene pólizas generadas");
          this.setState({ loading: false, polizasId: [] });
          return;
        }
        this.setState({ polizasId: res.data.polizasId }, () => this.cargarPolizas(false));
      })
      .catch(() => {
        message.error("Hubo un error y no se pudo consultar la trazabilidad de las pólizas");
        this.setState({ loading: false });
      });
  }

  cargarPolizas(detallada) {
    if (!this.state.polizasId || this.state.polizasId.length === 0) return;
    this.setState({ loading: true });
    axios
      .post(
        "polizas/datosPolizas",
        {
          polizasId: this.state.polizasId,
          empresaId: this.props.empresaId,
          detallada: !!detallada,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success !== 2) {
          message.error("Ha sucedido un error y no se cargaron bien las pólizas");
          this.setState({ loading: false });
          return;
        }
        // datosPolizas resuelve las pólizas en paralelo y las devuelve en orden arbitrario:
        // el orden de la hoja se decide aquí, no en el backend.
        const polizasOrdenadas = [...res.data.polizas].sort(
          (a, b) =>
            new Date(b.poliza.fechaRegistro) - new Date(a.poliza.fechaRegistro) ||
            (b.poliza.numeroTipoPoliza || 0) - (a.poliza.numeroTipoPoliza || 0)
        );
        // Con la trazabilidad prendida se imprime en el formato compacto (varias pólizas por
        // hoja); si no, el de siempre: una hoja por póliza con el formato oficial.
        const generador = this.state.trazabilidad ? polizasTrazabilidadPdf : polizasDocumentoPdf;
        const pdf = pdfMake.createPdf(generador({ polizas: polizasOrdenadas }));
        this.setState({ loading: false, pdf, detallada: !!detallada }, () =>
          this.abrirEnIframe(pdf)
        );
      })
      .catch(() => {
        message.error("Hubo un error y no se pudo generar el PDF de las pólizas");
        this.setState({ loading: false });
      });
  }

  componentWillUnmount() {
    this._desmontado = true;
    if (this.urlPdf) URL.revokeObjectURL(this.urlPdf);
  }

  // `pdf.open` navega el iframe con `location.href` y eso agrega una entrada al historial:
  // el `history.goBack()` de la pantalla que hizo el movimiento sólo regresaba el iframe.
  // Con `location.replace` el PDF se muestra sin dejar entrada.
  abrirEnIframe(pdf, intento = 0) {
    const frame =
      this.iframeRef.current?.contentWindow || window.frames[this.iframeName];
    if (frame) {
      pdf.getBlob((blob) => {
        if (this._desmontado) return;
        if (this.urlPdf) URL.revokeObjectURL(this.urlPdf);
        this.urlPdf = URL.createObjectURL(blob);
        frame.location.replace(this.urlPdf);
      });
      return;
    }
    if (intento < 10) {
      setTimeout(() => this.abrirEnIframe(pdf, intento + 1), 100);
    }
  }

  onCambiarDetallada(checked) {
    this.setState({ detallada: checked });
    this.cargarPolizas(checked);
  }

  // Sólo en modo movimiento. El modal no sabe de qué documento son estas pólizas —
  // NotificacionPolizas se llama desde decenas de pantallas y ninguna manda el documento—, así
  // que el backend lo deduce al revés a partir de los mismos ids.
  onCambiarTrazabilidad(marcado) {
    if (!marcado) {
      this.setState({ trazabilidad: false, polizasId: this.props.polizasId }, () =>
        this.cargarPolizas(this.state.detallada)
      );
      return;
    }

    this.setState({ loading: true });
    axios
      .post(
        "polizas/polizasDeTrazabilidadMovimiento",
        {
          polizasId: this.props.polizasId,
          empresaId: this.props.empresaId,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        const polizasId = res.data?.polizasId;
        if (!polizasId?.length) {
          message.warning("No se pudo resolver la trazabilidad de este movimiento");
          this.setState({ loading: false });
          return;
        }
        if (polizasId.length <= (this.props.polizasId?.length || 0)) {
          message.info("Este movimiento no tiene más pólizas relacionadas");
        }
        // La vista compacta no pinta los documentos vinculados, que es lo único que agrega
        // `detallada`: se carga siempre sin él para no traer datos que no se van a imprimir.
        this.setState({ trazabilidad: true, polizasId }, () => this.cargarPolizas(false));
      })
      .catch(() => {
        message.error("Hubo un error y no se pudo consultar la trazabilidad de las pólizas");
        this.setState({ loading: false });
      });
  }

  descargarPdf() {
    if (this.state.pdf) {
      const base = this.props.titulo || "Polizas";
      this.state.pdf.download(
        (this.state.trazabilidad ? base + " - Trazabilidad" : base) + ".pdf"
      );
    }
  }

  handleCancel = () => {
    this.props.onCancel();
  };

  render() {
    return (
      <ModalItem
        title={this.props.titulo || "Pólizas del Documento"}
        visible={this.props.visible}
        spinning={this.state.loading}
        onCancel={this.handleCancel}
        paddingTop
        width={1000}
        footer={null}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Tooltip
              title={
                this.state.trazabilidad
                  ? "La vista de trazabilidad no muestra los documentos vinculados"
                  : null
              }
            >
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>Detallada</span>
                <Switch
                  checked={this.state.detallada}
                  onChange={this.onCambiarDetallada.bind(this)}
                  loading={this.state.loading}
                  disabled={
                    this.state.trazabilidad ||
                    !this.state.polizasId ||
                    this.state.polizasId.length === 0
                  }
                />
              </span>
            </Tooltip>
            {/* Sólo en modo movimiento: en modo documento la trazabilidad ya se muestra siempre. */}
            {this.props.polizasId?.length > 0 && (
              <Checkbox
                checked={this.state.trazabilidad}
                disabled={this.state.loading}
                onChange={(e) => this.onCambiarTrazabilidad(e.target.checked)}
              >
                Ver pólizas de la trazabilidad
              </Checkbox>
            )}
          </span>
          <Space>
            <ButtonItem
              icon={<DownloadOutlined />}
              type="primary"
              lessTime
              disabled={!this.state.pdf}
              onClick={() => this.descargarPdf()}
            >
              Descargar PDF
            </ButtonItem>
            <ButtonItem danger lessTime onClick={this.handleCancel}>
              Cerrar
            </ButtonItem>
          </Space>
        </div>
        <iframe
          ref={this.iframeRef}
          title={"Polizas del Documento PDF"}
          style={{ width: "100%", height: "75vh", border: "1px solid #d9d9d9" }}
          id={this.iframeName}
          name={this.iframeName}
        />
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(ModalPolizasDocumento);
